import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Native-style auto-dismissing notification pop-ups.
// Polls the given endpoint, pops UNSEEN notifications (newest-first, capped),
// then marks them "seen" in the backend so they never re-pop or duplicate (synced across devices).
export function useNotificationPopups({ enabled, listUrl, markSeenUrl, pollMs = 20000, maxPops = 5, duration = 4000 }) {
  const poppedRef = useRef(new Set());
  const firstRunRef = useRef(true);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let timer = null;

    const poll = async () => {
      try {
        const res = await axios.get(listUrl);
        const items = Array.isArray(res.data) ? res.data : (res.data?.notifications || []);
        const unseen = items
          .filter((n) => n && n.id && !n.seen && !poppedRef.current.has(n.id))
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        if (unseen.length === 0) return;

        // Only visually pop the newest few; mark the rest seen silently (no storm).
        const toPop = unseen.slice(0, maxPops);
        const allIds = unseen.map((n) => n.id);
        unseen.forEach((n) => poppedRef.current.add(n.id));

        // Show oldest-of-the-batch first so the newest ends up on top of the stack.
        [...toPop].reverse().forEach((n, i) => {
          setTimeout(() => {
            if (stopped) return;
            toast(n.title || 'Notification', {
              description: n.message || '',
              duration,
              id: `notif-${n.id}`,
              closeButton: true,
            });
          }, i * 350);
        });

        if (allIds.length) {
          try { await axios.put(markSeenUrl, { ids: allIds }); } catch { /* retry next poll */ }
        }
      } catch { /* ignore transient errors */ }
    };

    // Small delay on first run so auth headers/session are ready.
    const startDelay = firstRunRef.current ? 1500 : 0;
    firstRunRef.current = false;
    const kickoff = setTimeout(poll, startDelay);
    timer = setInterval(poll, pollMs);

    return () => { stopped = true; clearTimeout(kickoff); if (timer) clearInterval(timer); };
  }, [enabled, listUrl, markSeenUrl, pollMs, maxPops, duration]);
}
